import React, { useEffect, useRef, useState } from "react";
import { useSocketChannel } from "src/_common/hooks/custom/useSocketChannel";
import { SOCKET_CHANNEL } from "src/_config";
import { useAppMakePickModalApiResponseSelector } from "src/_common/hooks/selectors/eventSelector";
import { useAppUserDetailsSelector } from "src/_common/hooks/selectors/userSelector";
import { useEventApi } from "src/_common/hooks/actions/event/appEventApiHook";
import { Modal } from "react-bootstrap";
import UpdateEmailModal from "src/components/events/modal/updateEmailModal";
import ViewPicksModal from "src/components/common/Modal/ViewPicksModal";

interface RodeoEvent {
  id: string;
  name: string;
  status: "picked" | "unpicked";
  selectedRider?: {
    name: string;
    avatar: string;
  };
}

interface MakePicksModalProps {
  isOpen: boolean;
  onClose: () => void;
  className?: string;
  onPicksChange?: () => void; // Callback when picks are updated
  eventData?: {
    rodeo_event_id?: string;
    performance_id?: string;
    group_id?: string;
    title: string;
    location: string;
    date: string;
    events?: RodeoEvent[];
  };
}

const SHOWN_MODAL_KEY_PREFIX = "RODEO_SHOWN_PICKED_TEAM_MODAL";

const MakePicksModal: React.FC<MakePicksModalProps> = ({
  isOpen,
  onClose,
  className = "",
  onPicksChange,
  eventData,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const eventsListRef = useRef<HTMLDivElement>(null);
  const tabsScrollRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [showLegend, setShowLegend] = useState(false);
  const eventApi = useEventApi();
  const noRideRpEventData = useSocketChannel<any>(SOCKET_CHANNEL.NO_RIDE_RP_EVENT);
  const eventContestantList = useAppMakePickModalApiResponseSelector();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pickedRiders, setPickedRiders] = useState<{ [eventId: string]: string }>({});
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showViewPicksModal, setShowViewPicksModal] = useState(false);
  const [showCongrats, setShowCongrats] = useState(false);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const userDetails = useAppUserDetailsSelector();
  // Fetch events from API when modal opens
  useEffect(() => {
    setLoading(true);
    setError(null);
    const params = {
      rodeo_event_id: eventData?.rodeo_event_id,
      performance_id: eventData?.performance_id,
      group_id: eventData?.group_id,
    };
    eventApi.callGetRodeoEventEvents(
      params,
      (_msg: string, _resp: any) => {
        setLoading(false);
        setError(null);
      },
      (_err: string) => {
        setLoading(false);
        setError("Failed to fetch rodeo event events");
      },
    );
  }, []);

  // Refresh when noRideRpEvent fires and matches this modal's rodeo_event_id + performance_id + event_id
  useEffect(() => {
    if (!noRideRpEventData || !isOpen) return;
    const matchesRodeoEvent =
      !noRideRpEventData.rodeo_event_id ||
      String(noRideRpEventData.rodeo_event_id) === String(eventData?.rodeo_event_id);
    const matchesPerformance =
      !noRideRpEventData.performance_id ||
      String(noRideRpEventData.performance_id) === String(eventData?.performance_id);
    if (!matchesRodeoEvent || !matchesPerformance) return;
    setLoading(true);
    setError(null);
    eventApi.callGetRodeoEventEvents(
      {
        rodeo_event_id: eventData?.rodeo_event_id,
        performance_id: eventData?.performance_id,
        group_id: eventData?.group_id,
      },
      (_msg: string, _resp: any) => {
        setLoading(false);
        setError(null);
      },
      (_err: string) => {
        setLoading(false);
        setError("Failed to fetch rodeo event events");
      },
    );
  }, [noRideRpEventData, isOpen]);

  useEffect(() => {
    if (eventContestantList?.event_dtls && eventContestantList.event_dtls.length > 0) {
      // Only reset to first tab if current activeTab doesn't exist in the new data
      // (i.e., modal reopened for a different event). Keep current tab on socket refreshes.
      const tabExists =
        activeTab && eventContestantList.event_dtls.some((event: any) => String(event.id) === String(activeTab));
      if (!tabExists) {
        setActiveTab(eventContestantList.event_dtls[0].id);
      }
    }
  }, [eventContestantList]);

  const checkScrollArrows = () => {
    const el = tabsScrollRef.current;
    if (!el) return;
    setShowLeftArrow(el.scrollLeft > 0);
    setShowRightArrow(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  useEffect(() => {
    checkScrollArrows();
    const el = tabsScrollRef.current;
    el?.addEventListener("scroll", checkScrollArrows);
    window.addEventListener("resize", checkScrollArrows);
    return () => {
      el?.removeEventListener("scroll", checkScrollArrows);
      window.removeEventListener("resize", checkScrollArrows);
    };
  }, [eventContestantList]);

  const handleTabsScrollLeft = () => {
    tabsScrollRef.current?.scrollBy({ left: -200, behavior: "smooth" });
  };

  const handleTabsScrollRight = () => {
    tabsScrollRef.current?.scrollBy({ left: 200, behavior: "smooth" });
  };

  // Scroll active tab into full view when activeTab changes
  useEffect(() => {
    if (!activeTab || !tabsScrollRef.current) return;
    const activeEl = tabsScrollRef.current.querySelector(".status-tag.active") as HTMLElement | null;
    if (activeEl) {
      activeEl.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
    }
  }, [activeTab]);

  const handlePickClick = (riderId: string) => {
    // Check if winning_declare is 0 and status is not 1 (picks are still allowed)
    const currentEvent = getFilteredEventsContestant();
    if (currentEvent?.winning_declare !== 0 || currentEvent?.status === 1) {
      console.log("Cannot pick: Picks are closed for this event");
      return;
    }

    const activeTabStr = String(activeTab);
    const riderIdStr = String(riderId);

    // Optimistic UI update - show pick immediately
    const updatedPicks = { ...pickedRiders, [activeTabStr]: riderIdStr };
    setPickedRiders(updatedPicks);

    const params = {
      rider_id: riderId,
      event_id: activeTab,
      performance_id: eventData?.performance_id,
      rodeo_event_id: eventData?.rodeo_event_id,
    };

    eventApi.callRodeoEventUserPick(
      params,
      (_msg: string, _resp: any) => {
        //console.log("Pick successful:", resp);
        // Call the callback to update pick counts in parent component
        onPicksChange?.();
        // Auto-advance to next unpicked event
        const nextUnpickedEvent = eventContestantList?.event_dtls?.find(
          (event: any) => !event.ispicked && !updatedPicks[String(event.id)],
        );
        if (nextUnpickedEvent) {
          setActiveTab(nextUnpickedEvent.id);
          if (eventsListRef.current) {
            eventsListRef.current.scrollTop = 0;
          }
        }
        // Check if all events are now picked
        const allPicked = eventContestantList?.event_dtls?.every(
          (event: any) => event.ispicked || updatedPicks[String(event.id)],
        );
        const performanceKey = String(eventData?.performance_id ?? "");
        if (allPicked && performanceKey) {
          const shownModalKey = `${SHOWN_MODAL_KEY_PREFIX}_${userDetails?.id ?? "guest"}`;
          let shownList: string[] = [];
          try {
            shownList = JSON.parse(localStorage.getItem(shownModalKey) || "[]");
          } catch {
            shownList = [];
          }
          if (!shownList.includes(performanceKey)) {
            shownList.push(performanceKey);
            localStorage.setItem(shownModalKey, JSON.stringify(shownList));
            openPickedTeamModal();
          }
        }
      },
      (err: string) => {
        console.error("Pick failed:", err);
        // Revert optimistic update on failure
        setPickedRiders((prev) => {
          const reverted = { ...prev };
          delete reverted[activeTabStr];
          return reverted;
        });
      },
    );
  };

  const handleTabClick = (tabType: string) => {
    setActiveTab(tabType);
    getFilteredEventsContestant();
    // Reset scroll position to top of rider list when switching events
    if (eventsListRef.current) {
      eventsListRef.current.scrollTop = 0;
    }
  };

  const openPickedTeamModal = () => {
    // console.log("Opening view picks modal with performance ID:", eventData?.performance_id);
    setShowCongrats(true);
    setShowViewPicksModal(true);
    // Open email modal on top if user has no email
    if (!userDetails?.email) {
      setShowEmailModal(true);
    }
  };

  const closePickedTeamModal = () => {
    setShowViewPicksModal(false);
    setShowCongrats(false);
  };

  // Use API events if available, otherwise fallback to static
  // const eventsList = (apiEvents && apiEvents.length > 0 ? apiEvents : eventData?.events) || [];
  // // eventsList now uses apiEvents from selector

  const getFilteredEventsContestant = () => {
    if (!eventContestantList?.event_dtls || !activeTab) return [];

    const filteredEvent = eventContestantList.event_dtls.filter((event: any) => event.id === activeTab)?.[0];

    return filteredEvent || [];
  };

  if (!isOpen) return null;

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      backdrop="static"
      keyboard={false}
      className={`make-picks-modal-new ${className}`}
      centered
      size="lg"
    >
      <Modal.Body className="p-0">
        <div
          ref={modalRef}
          className="make-picks-modal-new-content"
          role="dialog"
          aria-modal="true"
          aria-label="Make Picks Modal"
          tabIndex={-1}
        >
          {/* Close button */}
          <button className="btn-close-modal" onClick={onClose} aria-label="Close modal">
            ×
          </button>

          {/* Header */}
          <div className="modal-header-section">
            {eventContestantList?.performance_dtls?.avatar?.thumb ? (
              <div className="event-logo">
                <img src={eventContestantList?.performance_dtls?.avatar.thumb} alt="Avatar" />
              </div>
            ) : null}
            <div className="event-details">
              <h2 className="event-title">{eventContestantList?.group_dtls?.title}</h2>
              <strong className="text_yellow">{eventContestantList?.rodeo_event_dtls?.title}</strong>
              <p className="event-location">
                {eventContestantList?.rodeo_event_dtls?.location} {eventContestantList?.performance_dtls?.title}
              </p>
            </div>
          </div>

          {/* Status tags */}
          <div className="tabs-scroll-wrapper">
            {showLeftArrow && (
              <button
                className="tabs-scroll-btn tabs-scroll-btn-left"
                onClick={handleTabsScrollLeft}
                aria-label="Scroll left"
              >
                ‹
              </button>
            )}
            <div className="status-tags-section" ref={tabsScrollRef}>
              {eventContestantList?.event_dtls.map((events: any, index: number) => {
                // const isEventPicked = events.ispicked || pickedRiders[events.id];

                // Get user's picked rider ID from either API or local optimistic state
                const userPickedRiderId = events.ispicked?.rider_id ?? pickedRiders[String(events.id)];

                // Check if user picked a winning rider for this event
                const userPickedWinningRider =
                  userPickedRiderId &&
                  events.riders?.some(
                    (rider: any) => rider.is_winner === 1 && String(userPickedRiderId) === String(rider.id),
                  );

                // Check if user picked a failed rider for this event
                const userPickedFailedRider = events.riders?.some(
                  (rider: any) =>
                    rider.is_winner === 1 && (!userPickedRiderId || String(userPickedRiderId) !== String(rider.id)),
                );

                // Check if user has picked (either from API or runtime state)
                const hasPicked = events.ispicked || pickedRiders[String(events.id)];

                return (
                  <span
                    key={index} //${!isEventPicked ? "grey-label" : "picks-made"}
                    className={`status-tag ${
                      events.no_one_rides
                        ? "red-label"
                        : !hasPicked && events.winning_declare == 0
                          ? "unpicked-label"
                          : hasPicked && events.winning_declare == 0
                            ? "picked-label"
                            : events.winning_declare == 1 && userPickedWinningRider
                              ? "green-label"
                              : events.winning_declare == 1 && userPickedFailedRider
                                ? "red-label"
                                : ""
                    } ${activeTab === events.id ? "active" : ""}`}
                    onClick={() => handleTabClick(events.id)}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleTabClick(events.id);
                      }
                    }}
                  >
                    {events.no_one_rides ? (
                      <img src="/img/tab-icon-cross.svg" />
                    ) : activeTab === events.id ? (
                      <img src="/img/tab-icon-loading.svg" />
                    ) : !hasPicked && events.winning_declare == 0 ? ( // Pick is open but you have not selected.
                      <img src="/img/tab-icon-dot-circle.svg" />
                    ) : hasPicked && events.winning_declare == 0 ? ( // You've made a pick, but winner hasn't been declared yet.
                      <img src="/img/tab-icon-check.svg" />
                    ) : events.winning_declare == 1 && userPickedWinningRider ? (
                      <img src="/img/tab-icon-check.svg" />
                    ) : events.winning_declare == 1 && userPickedFailedRider ? (
                      <img src="/img/tab-icon-cross.svg" />
                    ) : null}

                    {events.event_name}
                  </span>
                );
              })}
            </div>
            {showRightArrow && (
              <button
                className="tabs-scroll-btn tabs-scroll-btn-right"
                onClick={handleTabsScrollRight}
                aria-label="Scroll right"
              >
                ›
              </button>
            )}
          </div>

          {/* Show Legend Toggle */}
          <div className="legend-section">
            <button
              className="show-legend-btn"
              onClick={() => setShowLegend(!showLegend)}
              aria-expanded={showLegend}
              aria-controls="legend-content"
            >
              <span>Show Legend</span>
              <i className={`fas fa-chevron-${showLegend ? "up" : "down"}`}></i>
            </button>

            {/* Legend Content */}
            {showLegend && (
              <div id="legend-content" className="legend-content">
                <div className="legend-item">
                  <div className="legend-icon-container info-icon">
                    <img src="/img/icon-1.png" />
                  </div>
                  <span className="legend-text">Pick is open but you have not selected.</span>
                </div>

                <div className="legend-item">
                  <div className="legend-icon-container check-icon">
                    <img src="/img/icon-2.png" />
                  </div>
                  <span className="legend-text">You&#39;ve made a pick, but winner hasn&#39;t been declared yet.</span>
                </div>

                <div className="legend-item d-none">
                  <div className="legend-icon-container clock-icon">
                    <img src="/img/icon-3.png" />
                  </div>
                  <span className="legend-text">
                    You&#39;ve made a pick, winner hasn&#39;t been declared yet, but picking is now closed.
                  </span>
                </div>

                <div className="legend-item">
                  <div className="legend-icon-container winner-icon">
                    <img src="/img/icon-4.png" />
                  </div>
                  <span className="legend-text">Picking is closed and your pick won.</span>
                </div>

                <div className="legend-item">
                  <div className="legend-icon-container loser-icon">
                    <img src="/img/icon-5.png" />
                  </div>
                  <span className="legend-text">
                    Picking is closed and you&#39;ve made no picks, or your pick didn&#39;t win.
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Riders list */}
          <div className="events-list-section" ref={eventsListRef}>
            {loading && <div className="loader">Loading events...</div>}
            {/* {error && <div className="error-message">{error}</div>} */}
            {!loading && !error && getFilteredEventsContestant()?.riders?.length === 0 && (
              <div className="no-events">No events available.</div>
            )}
            {!loading &&
              !error &&
              getFilteredEventsContestant()?.riders?.map((rider: any, index: number) => (
                <div key={index} className={`event-item ${rider.status} ${rider.is_winner === 1 ? "winner" : ""}`}>
                  <div className="event-info">
                    <div className="event-details-main">
                      {rider.rider && (
                        <div className="selected-rider">
                          <img
                            src={rider.rider.id === 0 ? rider?.contestant_dtls?.avatar?.thumb : rider.rider.thumb}
                            alt={rider.name}
                            className="rider-avatar"
                          />
                          <div className="wrap-riders-det">
                            <span className="rider-name">{rider.name}</span>
                            {rider.is_winner === 1 ? <span className="rider-winner-text">Winner</span> : null}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="event-actions">
                    {rider.is_winner === 1 ? (
                      <span className="winner-status">
                        <i className="fas fa-trophy"></i>
                      </span>
                    ) : pickedRiders[String(activeTab!)] === String(rider.id) ||
                      (pickedRiders[String(activeTab!)] === undefined &&
                        getFilteredEventsContestant()?.ispicked?.rider_id === rider.id) ? (
                      <span className="picked-status">Picked</span>
                    ) : getFilteredEventsContestant()?.winning_declare !== 0 ||
                      getFilteredEventsContestant()?.status === 1 ? (
                      <button className="btn-pick disabled" disabled>
                        Closed
                      </button>
                    ) : (
                      <button className="btn-pick" onClick={() => handlePickClick(rider.id)}>
                        Pick
                      </button>
                    )}
                  </div>
                </div>
              ))}
          </div>
        </div>
      </Modal.Body>
      <ViewPicksModal
        isOpen={showViewPicksModal}
        onClose={closePickedTeamModal}
        rodeoEventId={eventData?.rodeo_event_id}
        performanceId={eventData?.performance_id}
        groupId={eventData?.group_id}
        showCongrats={showCongrats}
      />
      <UpdateEmailModal shouldShow={showEmailModal} onClose={() => setShowEmailModal(false)} />
    </Modal>
  );
};

export default MakePicksModal;
